/* eslint-disable @typescript-eslint/naming-convention */
import { queueMacrotask } from 'module/helpers/macrotask';
import { Breadcrumb } from 'module/ui/components/atoms/breadcrumb';
import { Heading03 } from 'module/ui/components/styles';
import { BodyShort01 } from 'module/ui/ozone/atoms';
import { Typography } from 'antd';
import { FC, ReactNode, useEffect, useState } from 'react';
import styled, { css } from 'styled-components';
import { Sticky } from '../../sticky';

const { Paragraph } = Typography;

// AJ asked me to give 590 as font-weight for the title
const TitleComponent = styled.h4.attrs<{ 'data-cy'?: string }>({
  'data-cy': 'page.header.title',
})`
  ${Heading03};
`;

const Icon = styled.span.attrs<{ 'data-cy'?: string }>({
  'data-cy': 'page.header.title.icon',
})``;

const Text = styled.span.attrs<{ 'data-cy'?: string }>({
  'data-cy': 'page.header.title.text',
})``;

const SubText = styled(Paragraph).attrs<{ 'data-cy'?: string }>({
  'data-cy': 'page.header.title.subtext',
})``;

type Title = typeof TitleComponent & {
  Icon: typeof Icon;
  Text: typeof Text;
};

const Title: Title = TitleComponent as any;

Title.Icon = Icon;
Title.Text = Text;

const Tabs = styled.div``;

export const Captions = styled.div``;

interface HeaderProps {
  title: ReactNode;
  /** @deprecated */
  compact?: boolean;
  tabs?: ReactNode;
  sticky?: boolean;
  breadcrumbs?: ReactNode;
  subText?: ReactNode;
  isBigIcon?: boolean;
  actions?: ReactNode;
}
const Actions = styled.div``;
const Content = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;
const FirstRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const HeaderWrapper = styled.div<{
  compact?: boolean;
  isBigIcon?: boolean;
  hasTabs?: boolean;
}>`
  margin: 0;
  padding: var(--spacing-05) var(--spacing-06);

  ${Captions} {
    display: flex;
    transition: padding var(--animation-duration-medium) ease;
  }

  ${Breadcrumb} {
    + ${Captions} {
      padding-top: 0;
    }
  }

  ${Title} {
    display: inline-flex;
    align-items: center;
    transition: transform var(--animation-duration-medium) ease;
    transform: scale(1);
    transform-origin: 0;
    margin-bottom: 0;
  }

  ${Title} > ${Icon} {
    margin-right: ${({ isBigIcon }) =>
      isBigIcon ? 'var(--layout-01)' : 'var(--spacing-03)'};
    display: flex;
  }

  ${SubText} {
    ${BodyShort01};
    color: var(--text-secondary);
    margin-bottom: 0px;
  }
  ${Actions} {
    flex-shrink: 0;
  }
  ${({ hasTabs }) =>
    hasTabs
      ? css`
          padding-bottom: 0;
          ${FirstRow} {
            margin-bottom: var(--spacing-05);
          }
        `
      : ''};
`;

const HeaderComponent: FC<React.PropsWithChildren<HeaderProps>> = ({
  sticky,
  title,
  tabs,
  breadcrumbs,
  subText,
  actions,
  ...props
}) => {
  const [scrolled, setScrolled] = useState(false);
  const isCompact = true;

  useEffect(() => {
    if (!scrolled && isCompact) {
      queueMacrotask(() => setScrolled(true));
    }
  }, [scrolled, setScrolled, isCompact]);
  const header = (
    <HeaderWrapper
      id="page_header"
      data-cy="page_header"
      compact={isCompact}
      hasTabs={tabs != null}
      {...props}
    >
      <FirstRow>
        <Content>
          {breadcrumbs && <Breadcrumb>{breadcrumbs}</Breadcrumb>}
          <Captions>{title}</Captions>
          {subText && <SubText>{subText}</SubText>}
        </Content>
        <Actions>{actions}</Actions>
      </FirstRow>
      {tabs}
    </HeaderWrapper>
  );

  return sticky ? (
    <Sticky top={0} layer={9}>
      {header}
    </Sticky>
  ) : (
    header
  );
};

interface Props {
  $white?: boolean;
}
export const StyledHeader = styled(HeaderComponent)<Props>`
  background: ${'var(--white-100)'};
`;

type Header = typeof StyledHeader & {
  Title: typeof Title;
  Tabs: typeof Tabs;
};

const Header: Header = StyledHeader as any;

Header.Title = Title;
Header.Tabs = Tabs;

export { Header };
